import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SUPER_ADMIN = "kovid.verma@ssmch.in";

export default function AccessRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [tab, setTab] = useState("pending"); // pending | users

  const isSuperAdmin = user?.email === SUPER_ADMIN;
  const isAdmin = user?.role === "admin" || user?.role === "confidential";

  const load = async () => {
    try { const { data } = await api.get("/admin/access-requests"); setRequests(data); } catch {}
    try { const { data } = await api.get("/admin/users"); setUsers(data); } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const approve = async (userId) => {
    setActionLoading(userId);
    try {
      await api.post(`/admin/access-requests/${userId}/approve`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    setActionLoading(null);
  };

  const reject = async (userId, email) => {
    if (!window.confirm(`Reject and remove ${email}?`)) return;
    setActionLoading(userId);
    try {
      await api.post(`/admin/access-requests/${userId}/reject`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    setActionLoading(null);
  };

  const elevate = async (userId, email) => {
    if (!window.confirm(`Elevate ${email} to Admin?`)) return;
    setActionLoading(userId);
    try {
      await api.post(`/admin/users/${userId}/elevate`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    setActionLoading(null);
  };

  const demote = async (userId, email) => {
    if (!window.confirm(`Demote ${email} back to Operator?`)) return;
    setActionLoading(userId);
    try {
      await api.post(`/admin/users/${userId}/demote`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    setActionLoading(null);
  };

  if (!isAdmin) return <div style={{padding:40,textAlign:'center',color:'var(--text-3)'}}>Admin access required.</div>;

  return (
    <div data-testid="view-access-requests">
      <div className="topbar">
        <div>
          <h1 className="pg-title">Access Management</h1>
          <div className="pg-sub">Approve requests and manage user roles</div>
        </div>
        <div className="topbar-meta">
          <span className="meta-chip" onClick={load} style={{cursor:'pointer'}}>Refresh</span>
        </div>
      </div>

      <div style={{display:'flex',gap:0,marginBottom:16,borderBottom:'1px solid var(--line)'}}>
        {[{key:'pending',label:`Pending Requests (${requests.length})`},{key:'users',label:`Active Users (${users.length})`}].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{padding:'10px 20px',fontSize:13,fontWeight:tab===t.key?600:400,border:'none',borderBottom:tab===t.key?'2px solid var(--text-1)':'2px solid transparent',background:'transparent',cursor:'pointer',color:tab===t.key?'var(--text-1)':'var(--text-3)'}}
            data-testid={`tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{padding:40,textAlign:'center'}}><div className="spinner"/></div>
      ) : tab === "pending" ? (
        requests.length === 0 ? (
          <div style={{padding:'60px 20px',textAlign:'center'}}>
            <div style={{fontSize:40,marginBottom:12,opacity:0.3}}>&#10003;</div>
            <div style={{fontSize:16,color:'var(--text-2)',fontFamily:'var(--serif)'}}>No pending requests</div>
            <div style={{fontSize:13,color:'var(--text-3)',marginTop:6}}>All access requests have been processed</div>
          </div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {requests.map(r => (
              <div key={r.user_id} className="card" style={{display:'flex',alignItems:'center',gap:16,padding:16}} data-testid={`request-${r.user_id}`}>
                <div style={{width:44,height:44,borderRadius:'50%',background:'linear-gradient(135deg, #ECEAE4, #D9D2C3)',display:'grid',placeItems:'center',fontFamily:'var(--mono)',fontSize:14,fontWeight:500,flexShrink:0}}>
                  {(r.name || '?')[0].toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:500,fontSize:14}}>{r.name}</div>
                  <div style={{fontSize:12,color:'var(--text-2)',fontFamily:'var(--mono)',marginTop:2}}>{r.email}</div>
                  {r.created_at && <div style={{fontSize:11,color:'var(--text-3)',marginTop:2}}>Requested: {new Date(r.created_at).toLocaleString()}</div>}
                </div>
                <div style={{display:'flex',gap:8,flexShrink:0}}>
                  <button className="btn primary" disabled={actionLoading===r.user_id} onClick={() => approve(r.user_id)} data-testid={`approve-${r.user_id}`} style={{fontSize:12,padding:'6px 16px'}}>
                    {actionLoading===r.user_id ? '...' : 'Approve'}
                  </button>
                  <button className="btn" disabled={actionLoading===r.user_id} onClick={() => reject(r.user_id, r.email)} data-testid={`reject-${r.user_id}`} style={{fontSize:12,padding:'6px 16px',color:'var(--bad)'}}>
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {users.map(u => (
            <div key={u.user_id} className="card" style={{display:'flex',alignItems:'center',gap:16,padding:14}} data-testid={`user-${u.user_id}`}>
              <div style={{width:40,height:40,borderRadius:'50%',background: u.role==='admin' ? 'linear-gradient(135deg, #C5A44E, #8B7332)' : 'linear-gradient(135deg, #ECEAE4, #D9D2C3)',display:'grid',placeItems:'center',fontFamily:'var(--mono)',fontSize:13,fontWeight:500,flexShrink:0,color: u.role==='admin' ? '#fff' : 'var(--text-1)'}}>
                {(u.name || '?')[0].toUpperCase()}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:500,fontSize:14}}>{u.name}</div>
                <div style={{fontSize:12,color:'var(--text-2)',fontFamily:'var(--mono)',marginTop:2}}>{u.email}</div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
                <span style={{fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',padding:'3px 10px',borderRadius:4,
                  background: u.role==='admin' ? 'rgba(197,164,78,0.12)' : 'var(--bg-elev)',
                  color: u.role==='admin' ? '#8B7332' : 'var(--text-3)',
                  border: `1px solid ${u.role==='admin' ? 'rgba(197,164,78,0.3)' : 'var(--line-soft)'}`}}>
                  {u.role}
                </span>
                {isSuperAdmin && u.email !== SUPER_ADMIN && (
                  u.role === "operator" ? (
                    <button className="btn" disabled={actionLoading===u.user_id} onClick={() => elevate(u.user_id, u.email)} data-testid={`elevate-${u.user_id}`}
                      style={{fontSize:11,padding:'4px 12px',color:'#8B7332',border:'1px solid rgba(197,164,78,0.4)'}}>
                      Make Admin
                    </button>
                  ) : u.role === "admin" ? (
                    <button className="btn" disabled={actionLoading===u.user_id} onClick={() => demote(u.user_id, u.email)} data-testid={`demote-${u.user_id}`}
                      style={{fontSize:11,padding:'4px 12px',color:'var(--bad)',border:'1px solid rgba(248,113,113,0.4)'}}>
                      Demote
                    </button>
                  ) : null
                )}
              </div>
            </div>
          ))}
          {!isSuperAdmin && (
            <div style={{fontSize:12,color:'var(--text-3)',marginTop:12,textAlign:'center'}}>
              Only <strong>kovid.verma@ssmch.in</strong> can elevate operators to admin or demote admins.
            </div>
          )}
        </div>
      )}

      {/* Purge seed/dummy data */}
      {isAdmin && tab === "users" && (
        <PurgeSeedData />
      )}
    </div>
  );
}


function PurgeSeedData() {
  const [pin, setPin] = useState("");
  const [status, setStatus] = useState(null); // null | "loading" | {ok, deleted} | {error}

  const purge = async () => {
    if (!window.confirm("This will permanently delete the 16 dummy faculty records (SSMCH-0001 to SSMCH-0016). Continue?")) return;
    setStatus("loading");
    try {
      const { data } = await api.post("/admin/purge-seed-data", { pin });
      setStatus(data);
    } catch (e) {
      setStatus({ error: e?.response?.data?.detail || "Failed" });
    }
  };

  return (
    <div style={{marginTop:32,padding:20,background:"rgba(248,113,113,0.04)",border:"1px solid rgba(248,113,113,0.15)",borderRadius:10}} data-testid="purge-seed-section">
      <div style={{fontSize:14,fontWeight:600,color:"#dc2626",marginBottom:6}}>Purge Dummy Faculty Records</div>
      <div style={{fontSize:12,color:"var(--text-2)",marginBottom:12}}>
        Delete the 16 sample records (SSMCH-0001 to SSMCH-0016) that were auto-generated during initial setup. This action is irreversible.
      </div>
      {status && status !== "loading" ? (
        status.error ? (
          <div style={{fontSize:13,color:"#dc2626"}} data-testid="purge-error">{status.error}</div>
        ) : (
          <div style={{fontSize:13,color:"#059669"}} data-testid="purge-result">Deleted {status.deleted} dummy records.</div>
        )
      ) : (
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <input type="password" className="form-input mono" placeholder="PIN" maxLength={4} value={pin}
            onChange={e => setPin(e.target.value.replace(/[^0-9]/g,""))}
            style={{width:80,textAlign:"center",fontSize:16,letterSpacing:6}} data-testid="purge-pin"/>
          <button className="btn" onClick={purge} disabled={pin.length < 4 || status === "loading"}
            style={{fontSize:12,padding:"8px 18px",color:"#dc2626",borderColor:"rgba(248,113,113,0.4)"}} data-testid="purge-btn">
            {status === "loading" ? "Deleting..." : "Purge 16 Dummy Records"}
          </button>
        </div>
      )}
    </div>
  );
}
